// entries/admin_extension_tokens/App.jsx
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ConfirmDialog from "./components/ConfirmDialog"
import FiltersBar from "./components/FiltersBar"
import GenerateTokenModal from "./components/GenerateTokenModal"
import OverviewStats from "./components/OverviewStats"
import Pagination from "./components/Pagination"
import UserDetailPanel from "./components/UserDetailPanel"
import UsersList from "./components/UsersList"

import {
  useGenerateAdminExtensionTokenMutation,
  useLazyGetAdminExtensionTokensIndexDataQuery,
  useRevokeAdminExtensionTokenMutation,
} from "../../../app/services/book/admin_extension_tokens"

const PER_PAGE = 20

const App = () => {
  const { t } = useTranslation()

  const [q, setQ] = useState("")
  const [status, setStatus] = useState("all")
  const [page, setPage] = useState(1)

  const [selectedUserId, setSelectedUserId] = useState(null)
  const [confirmRevokeUser, setConfirmRevokeUser] = useState(null)
  const [generatedFor, setGeneratedFor] = useState(null)
  const [generatingUserId, setGeneratingUserId] = useState(null)

  const [fetchIndexData, { data, isLoading, isFetching }] =
    useLazyGetAdminExtensionTokensIndexDataQuery()

  const [generateToken] = useGenerateAdminExtensionTokenMutation()
  const [revokeToken, { isLoading: isRevoking }] = useRevokeAdminExtensionTokenMutation()

  const queryParams = useMemo(() => {
    const params = { page, per_page: PER_PAGE }
    if (q.trim()) params.q = q.trim()
    if (status !== "all") params.status = status
    return params
  }, [q, status, page])

  useEffect(() => {
    fetchIndexData(queryParams)
  }, [fetchIndexData, queryParams])

  useEffect(() => {
    setPage(1)
  }, [q, status])

  const users = data?.users || []
  const meta = data?.meta
  const overview = data?.overview
  const isInitialLoading = isLoading && users.length === 0

  const handleGenerate = async (user) => {
    setGeneratingUserId(user.id)
    try {
      const res = await generateToken(user.id).unwrap()
      setGeneratedFor({ user, ...res })
    } catch (err) {
      setGeneratedFor({
        user,
        error: err?.data?.message || "tokens_limit_reached",
        max_tokens: err?.data?.max_tokens,
      })
    } finally {
      setGeneratingUserId(null)
    }
  }

  const handleRevokeConfirmed = async () => {
    if (!confirmRevokeUser) return
    await revokeToken(confirmRevokeUser.id).unwrap()
    setConfirmRevokeUser(null)
  }

  return (
    <div className="min-h-screen w-full bg-[#F4F6F9] py-6 md:py-10 px-4 md:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4 md:mb-6">
          <div>
            <h1 className="text-lg md:text-xl font-semibold text-[#1F2937]">
              {t("admin_extension_tokens.title")}
            </h1>
            <p className="text-sm text-[#9AA5B4] mt-0.5">{t("admin_extension_tokens.subtitle")}</p>
          </div>
        </div>

        <OverviewStats overview={overview} />

        <FiltersBar q={q} onQChange={setQ} status={status} onStatusChange={setStatus} />

        <div className="flex flex-col gap-3">
          {isInitialLoading && (
            <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
              {t("admin_extension_tokens.loading")}
            </div>
          )}

          {isFetching && !isInitialLoading && (
            <div className="text-sm text-[#9AA5B4] px-1">
              {t("admin_extension_tokens.updating")}
            </div>
          )}

          {!isInitialLoading && (
            <UsersList
              users={users}
              onGenerate={handleGenerate}
              onRevoke={setConfirmRevokeUser}
              onViewDetails={(user) => setSelectedUserId(user.id)}
              generatingUserId={generatingUserId}
            />
          )}

          {!isInitialLoading && users.length === 0 && (
            <div className="text-center py-20 text-[#9AA5B4] bg-white rounded-2xl border border-[#E7EAEF]">
              {t("admin_extension_tokens.no_users")}
            </div>
          )}

          {meta && meta.total_pages > 1 && <Pagination meta={meta} onPageChange={setPage} />}
        </div>
      </div>

      <UserDetailPanel
        userId={selectedUserId}
        onClose={() => setSelectedUserId(null)}
        onGenerate={handleGenerate}
        onRevokeAll={setConfirmRevokeUser}
      />

      <GenerateTokenModal generated={generatedFor} onClose={() => setGeneratedFor(null)} />

      <ConfirmDialog
        open={!!confirmRevokeUser}
        title={t("admin_extension_tokens.revoke_confirm_title")}
        message={t("admin_extension_tokens.revoke_confirm_message", {
          name: confirmRevokeUser?.name || confirmRevokeUser?.email,
        })}
        confirmLabel={t("admin_extension_tokens.revoke_confirm_button")}
        danger
        loading={isRevoking}
        onConfirm={handleRevokeConfirmed}
        onCancel={() => setConfirmRevokeUser(null)}
      />
    </div>
  )
}

export default App
